import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle, AlertCircle } from "lucide-react";

const PasswordReset = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [formData, setFormData] = useState({ password: "", confirmPassword: "" });
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    hasMinLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  useEffect(() => {
    validateToken();
  }, []);

  const validateToken = async () => {
    if (!token) {
      setValidating(false);
      setTokenValid(false);
      return;
    }

    try {
      await axios.post(`${API}/auth/validate-reset-token`, { token });
      setTokenValid(true);
    } catch (error) {
      setTokenValid(false);
      toast.error("Invalid or expired reset link");
    } finally {
      setValidating(false);
    }
  };

  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      hasMinLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    });
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, password: newPassword });
    checkPasswordStrength(newPassword);
  };

  const isPasswordValid = () => {
    return Object.values(passwordStrength).every(val => val === true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid()) {
      toast.error("Password does not meet complexity requirements");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: formData.password
      });
      toast.success("Password reset successful! You can now login with your new password.");
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0E366A] via-[#1a4d7a] to-[#0E366A] flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-gray-600">Validating reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0E366A] via-[#1a4d7a] to-[#0E366A] flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl text-red-600">Invalid Reset Link</CardTitle>
            <CardDescription>
              This password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0E366A] via-[#1a4d7a] to-[#0E366A] flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/lis_logo.png" 
              alt="London Islamic School" 
              className="h-16"
            />
          </div>
          <CardTitle className="text-2xl text-[#0E366A]">Reset Your Password</CardTitle>
          <CardDescription>
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input 
                id="password" 
                name="password" 
                type="password" 
                required 
                value={formData.password}
                onChange={handlePasswordChange}
                className="border-[#0E366A]/20 focus:border-[#F7B538]"
              />
            </div>

            {/* Password Strength Indicators */}
            <div className="space-y-2 text-sm">
              <p className="font-medium text-gray-700">Password Requirements:</p>
              <div className="space-y-1">
                <div className={`flex items-center ${passwordStrength.hasMinLength ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordStrength.hasMinLength ? <CheckCircle className="w-4 h-4 mr-2" /> : <span className="w-4 h-4 mr-2">○</span>}
                  At least 8 characters
                </div>
                <div className={`flex items-center ${passwordStrength.hasUpperCase ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordStrength.hasUpperCase ? <CheckCircle className="w-4 h-4 mr-2" /> : <span className="w-4 h-4 mr-2">○</span>}
                  One uppercase letter
                </div>
                <div className={`flex items-center ${passwordStrength.hasLowerCase ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordStrength.hasLowerCase ? <CheckCircle className="w-4 h-4 mr-2" /> : <span className="w-4 h-4 mr-2">○</span>}
                  One lowercase letter
                </div>
                <div className={`flex items-center ${passwordStrength.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordStrength.hasNumber ? <CheckCircle className="w-4 h-4 mr-2" /> : <span className="w-4 h-4 mr-2">○</span>}
                  One number
                </div>
                <div className={`flex items-center ${passwordStrength.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`}>
                  {passwordStrength.hasSpecialChar ? <CheckCircle className="w-4 h-4 mr-2" /> : <span className="w-4 h-4 mr-2">○</span>}
                  One special character (!@#$%^&*)
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input 
                id="confirmPassword" 
                name="confirmPassword" 
                type="password" 
                required 
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="border-[#0E366A]/20 focus:border-[#F7B538]"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#F7B538] hover:bg-[#e5a627] text-[#0E366A] font-semibold" 
              disabled={loading || !isPasswordValid()}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </Button>

            <Button 
              type="button"
              variant="outline"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Back to Login
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PasswordReset;
